import type { Gemoji } from "gemoji";
import { COMMENT_REACTIONS } from "@brev-io/common/src/constants/comment-reactions";

/** What the `:` typeahead offers and inserts: the glyph, shown with the shortcode that matched. */
export interface EmojiMatch {
  emoji: string;
  shortcode: string;
}

interface EmojiEntry {
  emoji: string;
  names: readonly string[];
  tags: readonly string[];
  descriptionWords: readonly string[];
  /** Position in `COMMENT_REACTIONS`, so the reaction set wins ties; `Infinity` for the rest. */
  boost: number;
}

export interface EmojiIndex {
  entries: readonly EmojiEntry[];
  byShortcode: ReadonlyMap<string, string>;
}

const MAX_EMOJI_RESULTS = 8;

/** A shortcode query the typeahead searches; a lone `:` and `:x` never open it. */
export const EMOJI_QUERY = /^[a-z0-9_+-]{2,}$/i;

/** A completed `:shortcode:` typed after whitespace or at the start of a line. */
export const COMPLETED_SHORTCODE = /(?:^|\s)(:([a-z0-9_+-]+):)$/i;

export function buildEmojiIndex(data: readonly Gemoji[]): EmojiIndex {
  const reactionRank = new Map<string, number>(COMMENT_REACTIONS.map((r, i) => [r.emoji, i]));
  const byShortcode = new Map<string, string>();
  const entries = data.map((item) => {
    for (const name of item.names) byShortcode.set(name, item.emoji);
    return {
      emoji: item.emoji,
      names: item.names,
      tags: item.tags,
      descriptionWords: item.description.toLowerCase().split(/\s+/),
      boost: reactionRank.get(item.emoji) ?? Number.POSITIVE_INFINITY
    };
  });
  return { entries, byShortcode };
}

/** Lower is better: exact shortcode, shortcode prefix, a `_` segment of one, then keyword and description words. */
function rankEntry(entry: EmojiEntry, query: string): { rank: number; shortcode: string } | null {
  let rank = Number.POSITIVE_INFINITY;
  let shortcode = entry.names[0];
  for (const name of entry.names) {
    const nameRank =
      name === query ? 0
      : name.startsWith(query) ? 1
      : name.split("_").some((part) => part.startsWith(query)) ? 2
      : Number.POSITIVE_INFINITY;
    if (nameRank < rank) {
      rank = nameRank;
      shortcode = name;
    }
  }
  if (rank === Number.POSITIVE_INFINITY) {
    if (entry.tags.some((tag) => tag.startsWith(query))) rank = 3;
    else if (entry.descriptionWords.some((word) => word.startsWith(query))) rank = 4;
    else return null;
  }
  return { rank, shortcode };
}

export function searchEmoji(index: EmojiIndex, rawQuery: string, limit = MAX_EMOJI_RESULTS): EmojiMatch[] {
  const query = rawQuery.toLowerCase();
  if (!query) return [];
  const ranked: { rank: number; boost: number; order: number; match: EmojiMatch }[] = [];
  index.entries.forEach((entry, order) => {
    const hit = rankEntry(entry, query);
    if (hit) ranked.push({ rank: hit.rank, boost: entry.boost, order, match: { emoji: entry.emoji, shortcode: hit.shortcode } });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.boost - b.boost || a.order - b.order);
  return ranked.slice(0, limit).map((r) => r.match);
}

export function emojiForShortcode(index: EmojiIndex, shortcode: string): string | undefined {
  return index.byShortcode.get(shortcode.toLowerCase());
}

let loaded: EmojiIndex | null = null;
let loading: Promise<EmojiIndex> | null = null;

/** The emoji table (~1,900 entries) is its own chunk, fetched the first time someone types a shortcode. */
export function loadEmojiIndex(): Promise<EmojiIndex> {
  if (loaded) return Promise.resolve(loaded);
  loading ??= import("gemoji").then(
    ({ gemoji }) => (loaded = buildEmojiIndex(gemoji)),
    (error: unknown) => {
      loading = null; // a later trigger retries
      throw error;
    }
  );
  return loading;
}

export function getLoadedEmojiIndex(): EmojiIndex | null {
  return loaded;
}
